import { Menu, MenuItem, MenuTrigger } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { ArrowDownSmallIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

import * as styles from './permission-role-menu.css';

export interface PermissionRoleMenuOption<Role extends string> {
  role: Role;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
}

export interface PermissionRoleMenuProps<Role extends string> {
  role: Role;
  label?: ReactNode;
  options: readonly PermissionRoleMenuOption<Role>[];
  onSelect: (role: Role) => void;
  disabled?: boolean;
  className?: string;
  triggerKind?: 'button' | 'menu-trigger';
  align?: 'start' | 'center' | 'end';
  contentStyle?: CSSProperties;
  testId?: string;
}

export function PermissionRoleMenu<Role extends string>({
  role,
  label,
  options,
  onSelect,
  disabled = false,
  className,
  triggerKind = 'button',
  align = 'end',
  contentStyle,
  testId,
}: PermissionRoleMenuProps<Role>) {
  const currentLabel =
    label ?? options.find(option => option.role === role)?.label ?? role;
  const items = (
    <>
      {options.map(option => (
        <MenuItem
          key={option.role}
          data-testid={testId ? `${testId}-option-${option.role}` : undefined}
          selected={option.role === role}
          disabled={disabled || option.disabled}
          onSelect={() => onSelect(option.role)}
        >
          <span
            className={styles.option}
            title={option.disabled ? option.disabledReason : undefined}
          >
            {option.label}
            {option.disabled && option.disabledReason ? (
              <span className={styles.disabledReason}>
                {option.disabledReason}
              </span>
            ) : null}
          </span>
        </MenuItem>
      ))}
    </>
  );

  if (triggerKind === 'menu-trigger') {
    return (
      <Menu contentOptions={{ align }} items={items}>
        <MenuTrigger
          data-testid={testId}
          className={clsx(styles.trigger, className)}
          variant="plain"
          disabled={disabled}
          contentStyle={contentStyle}
        >
          {currentLabel}
        </MenuTrigger>
      </Menu>
    );
  }

  return (
    <Menu contentOptions={{ align }} items={items}>
      <Button
        suffix={<ArrowDownSmallIcon />}
        variant="plain"
        className={clsx(styles.trigger, className)}
        disabled={disabled}
        data-testid={testId}
      >
        {currentLabel}
      </Button>
    </Menu>
  );
}
